// Eskom PARCC — dashboard compliance-level colour map.
//
// The monolith dashboard coloured its distribution bars and swatches from an
// inline `colorMap` rather than the `.c-*` badge classes (the bars are inline
// SVG/divs, not badges). We reproduce that exact palette here so the React
// distribution renders the same colours, and expose the canonical level order
// (enum order, IE → policy → housekeeping bands) the bars are drawn in.
import {
  COMPLIANCE_LEVELS,
  NEW_COMPLIANCE_LEVELS,
  type ComplianceLevel,
} from '../../domain/enums';

const NEUTRAL = '#a9c0d3';
// Tier colours, matching the monolith's colorMap: IE/high = red, policy = amber.
const IE_RED = '#d64545';
const POLICY_AMBER = '#f2a900';
const HOUSEKEEPING_BLUE = '#69c5e4';

const LEVEL_COLOURS: Record<string, string> = {
  Compliant: '#3bb273',
  'Self-Declared: Non-Compliance: Possible IE': IE_RED,
  'Self-Declared: Non-Compliant: Policy & Procedures': POLICY_AMBER,
  'Self-Declared: Non-Compliance Housekeeping': HOUSEKEEPING_BLUE,
  'Self-Declared: Not Applicable': '#7f8faa',
  'Non-Compliance: Possible IE': IE_RED,
  'Non-Compliant: Policy & Procedures': POLICY_AMBER,
  'Non-Compliance Housekeeping': HOUSEKEEPING_BLUE,
  'Not Applicable': '#7f8faa',
  'Pending Review': NEUTRAL,
  'Not Reviewed': NEUTRAL,
  Deleted: '#8b1e1e',
  'To delete': '#b26f00',
  // Newer compliance taxonomy — reuse the closest existing tier's colour so the bars
  // read consistently.
  //
  // FIXED R7 (27 Jul 2026): this block had the tiers INVERTED while its comment
  // claimed to mirror riskWeight. Per the 9 Jun NTCSA-legend alignment — encoded in
  // dashboard.ts IE_LEVELS/POLICY_LEVELS and gaps.ts gapTierOf — Possible
  // Non-Compliance is the PFMA/IE tier (weight 3) and a bare Non-Conformance is the
  // policy tier (weight 2), with Administrative Control Weakness as housekeeping.
  // The old mapping painted policy-tier rows IE-red and IE-tier rows policy-amber,
  // so the distribution chart contradicted every count beside it.
  'Possible Non-Compliance': IE_RED,
  'Self-Declared: Possible Non-Compliance': IE_RED,
  'Non-Conformance': POLICY_AMBER,
  'Self-Declared: Non-Conformance': POLICY_AMBER,
  'Non-Conformance - Administrative Control Weakness': HOUSEKEEPING_BLUE,
};

/** Bar/swatch colour for a compliance level, matching the monolith's colorMap. */
export function levelColour(level: string): string {
  return LEVEL_COLOURS[level] ?? NEUTRAL;
}

/**
 * The canonical compliance-level order for the distribution, minus the internal
 * lifecycle markers the monolith dashboard never charts (`Deleted` / `To delete`).
 * The newer taxonomy is appended after the legacy ladder so a transaction
 * classified under it still surfaces in the chart; levels with no records are
 * filtered out at render time, so this is a no-op until the new levels appear.
 */
export const DISTRIBUTION_LEVELS: readonly (ComplianceLevel | string)[] = [
  ...COMPLIANCE_LEVELS.filter((l) => l !== 'Deleted' && l !== 'To delete'),
  ...NEW_COMPLIANCE_LEVELS,
];
